import * as React from "react";
import { sub, startOfDay, endOfDay } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { DateRange } from "react-day-picker";
import { Separator } from "@/components/ui/separator";
import { Transition } from "@/components/ui/transition";
import { useTranslation } from "react-i18next";
import { Input } from "./input";
import { useState } from "react";
import { store } from "../../../store";
import {
    isRangeEqual,
    isRangeValid,
    lastMonth,
    lastWeek,
    thisMonth,
    thisWeek,
    today,
    yesterday,
} from "@/lib/time";

type QuickSelectorProps = React.PropsWithChildren & {
    active?: boolean;
    onClick?: () => void;
    disabled?: boolean;
};

const QuickSelector = ({
    children,
    active,
    onClick,
    disabled,
}: QuickSelectorProps): JSX.Element => {
    return (
        <button
            className={cn(
                "focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-pointer items-center rounded-xs py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-disabled:pointer-events-none",
                active && "bg-accent text-accent-foreground",
            )}
            onClick={onClick}
            disabled={disabled}
        >
            {children}
        </button>
    );
};

export type DateRangePickerProps = {
    range?: DateRange;
    onChange?: (date: DateRange) => void;
    className?: string;
};

export const DateRangePicker = ({
    range,
    onChange,
    className,
}: DateRangePickerProps): JSX.Element => {
    const { t } = useTranslation();

    const [from, setFrom] = useState<string>("");
    const [fromErrored, setFromErrored] = useState<boolean>(false);
    const [to, setTo] = useState<string>("");
    const [toErrored, setToErrored] = useState<boolean>(false);

    const [open, setOpen] = useState(false);

    const quickSelectors = [
        {
            label: t("component.dateRangePicker.today"),
            range: today(),
            active: range && isRangeEqual(range, today()),
        },
        {
            label: t("component.dateRangePicker.yesterday"),
            range: yesterday(),
            active: range && isRangeEqual(range, yesterday()),
        },
        {
            label: t("component.dateRangePicker.thisWeek"),
            range: thisWeek(),
            active: range && isRangeEqual(range, thisWeek()),
        },
        {
            label: t("component.dateRangePicker.lastWeek"),
            range: lastWeek(),
            active: range && isRangeEqual(range, lastWeek()),
        },
        {
            label: t("component.dateRangePicker.thisMonth"),
            range: thisMonth(),
            active: range && isRangeEqual(range, thisMonth()),
        },
        {
            label: t("component.dateRangePicker.lastMonth"),
            range: lastMonth(),
            active: range && isRangeEqual(range, lastMonth()),
        },
    ];
    const activeSelector = quickSelectors.find((s) => s.active);

    return (
        <>
            <Transition
                show={open}
                enter="transition-opacity duration-400"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-400"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div
                    className={cn("fixed top-0 left-0 z-10 h-full w-full bg-neutral-900/5")}
                    onClick={() => setOpen(false)}
                />
            </Transition>
            <Popover onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant={"outline"}
                        className={cn(
                            "w-[280px] justify-start text-left font-normal",
                            !range && "text-muted-foreground",
                            range != null &&
                                !isRangeValid(range) &&
                                "border-red-500 focus-visible:ring-red-500",
                            className,
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {activeSelector && activeSelector.label}
                        {range && !activeSelector && (
                            <>
                                {range.from && store.i18n.formatDateOnly(range.from)}{" "}
                                {range.to && <> - {store.i18n.formatDateOnly(range.to)}</>}
                            </>
                        )}
                        {!range && <span>{t("component.dateRangePicker.placeholder")}</span>}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3">
                    <div className="flex gap-2.5">
                        <div className="flex flex-col gap-2.5">
                            <Calendar
                                locale={store.i18n.languageInfo?.localeDateFns}
                                numberOfMonths={2}
                                defaultMonth={sub(new Date(), {
                                    months: 1,
                                })}
                                mode="range"
                                selected={range}
                                onSelect={(range) => {
                                    if (range?.from) {
                                        setFrom(store.i18n.formatDateOnly(range.from));
                                        setFromErrored(false);
                                    }

                                    if (range?.to) {
                                        setTo(store.i18n.formatDateOnly(range.to));
                                        setToErrored(false);
                                    }

                                    onChange?.({
                                        from: range?.from && startOfDay(range.from),
                                        to: range?.to && endOfDay(range.to),
                                    });
                                }}
                                initialFocus
                            />
                            <Separator />
                            <div className="flex items-center gap-2.5">
                                <Input
                                    errored={fromErrored}
                                    startIcon={CalendarIcon}
                                    placeholder={t("component.dateRangePicker.placeholderDate")}
                                    value={from}
                                    onChange={(e) => {
                                        const from = store.i18n.parseDateOnly(e.target.value);
                                        if (from) {
                                            setFromErrored(false);
                                            onChange?.({
                                                from,
                                                to: range?.to,
                                            });
                                        } else {
                                            setFromErrored(true);
                                        }
                                        setFrom(e.target.value);
                                    }}
                                />
                                <span className="text-muted-foreground">-</span>
                                <Input
                                    errored={toErrored}
                                    startIcon={CalendarIcon}
                                    placeholder={t("component.dateRangePicker.placeholderDate")}
                                    value={to}
                                    onChange={(e) => {
                                        const to = store.i18n.parseDateOnly(e.target.value);
                                        if (to) {
                                            setToErrored(false);
                                            onChange?.({
                                                from: range?.from,
                                                to,
                                            });
                                        } else {
                                            setToErrored(true);
                                        }
                                        setTo(e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                        <Separator orientation="vertical" />
                        <div className="flex flex-col gap-1">
                            {quickSelectors.map((s) => (
                                <QuickSelector
                                    key={s.label}
                                    active={s.active}
                                    onClick={() => {
                                        if (s.range?.from) {
                                            setFrom(store.i18n.formatDateOnly(s.range.from));
                                            setFromErrored(false);
                                        }
                                        if (s.range?.to) {
                                            setTo(store.i18n.formatDateOnly(s.range.to));
                                            setToErrored(false);
                                        }
                                        onChange?.(s.range);
                                    }}
                                >
                                    {s.label}
                                </QuickSelector>
                            ))}
                            <QuickSelector
                                active={range?.from != null && range.to != null && !activeSelector}
                                disabled
                            >
                                {t("component.dateRangePicker.custom")}
                            </QuickSelector>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </>
    );
};
